import React from "react";
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  multiFactor,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  TotpMultiFactorGenerator,
} from "firebase/auth";
import { CheckCircle2, Copy, KeyRound, ShieldCheck, Trash2 } from "lucide-react";
import { useAuth } from "@core/auth/AuthProvider";

const DEMO_MFA_STORAGE_KEY = "venpro_demo_mfa_enrolled.v1";

function errorMessage(error) {
  if (error?.code === "auth/invalid-verification-code") return "That authenticator code is incorrect.";
  if (error?.code === "auth/requires-recent-login") return "Please confirm your password before configuring 2-step verification.";
  if (error?.code === "auth/wrong-password" || error?.code === "auth/invalid-credential") return "Incorrect password. Please try again.";
  if (error?.code === "auth/user-token-expired") return "Your session changed. Sign in again to continue.";
  return error instanceof Error ? error.message : "Multi-factor authentication could not be updated.";
}

export function TotpMfaCard() {
  const { user, isDemoMode } = useAuth();
  const [factors, setFactors] = React.useState(() => (user && !isDemoMode ? [...multiFactor(user).enrolledFactors] : []));
  const [demoMfaEnrolled, setDemoMfaEnrolled] = React.useState(() => (
    isDemoMode && localStorage.getItem(DEMO_MFA_STORAGE_KEY) === "true"
  ));
  const [secret, setSecret] = React.useState(null);
  const [code, setCode] = React.useState("");
  const [needsPasswordReauth, setNeedsPasswordReauth] = React.useState(false);
  const [reauthPassword, setReauthPassword] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");

  const isEnabled = isDemoMode ? demoMfaEnrolled : factors.length > 0;

  const refresh = React.useCallback(() => {
    if (isDemoMode) {
      setDemoMfaEnrolled(localStorage.getItem(DEMO_MFA_STORAGE_KEY) === "true");
    } else if (user) {
      setFactors([...multiFactor(user).enrolledFactors]);
    }
  }, [isDemoMode, user]);

  React.useEffect(refresh, [refresh]);

  const startEnrollment = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    setNeedsPasswordReauth(false);

    if (isDemoMode) {
      setSecret({ secretKey: "JBSWY3DPEHPK3PXP" });
      setCode("");
      setBusy(false);
      return;
    }

    try {
      let session;
      try {
        session = await multiFactor(user).getSession();
      } catch (sessionErr) {
        if (sessionErr?.code === "auth/requires-recent-login") {
          const hasGoogle = user.providerData?.some((p) => p.providerId === "google.com");
          if (hasGoogle) {
            await reauthenticateWithPopup(user, new GoogleAuthProvider());
            session = await multiFactor(user).getSession();
          } else {
            setNeedsPasswordReauth(true);
            setBusy(false);
            return;
          }
        } else {
          throw sessionErr;
        }
      }
      setSecret(await TotpMultiFactorGenerator.generateSecret(session));
      setCode("");
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordReauth = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const cred = EmailAuthProvider.credential(user.email, reauthPassword);
      await reauthenticateWithCredential(user, cred);
      const session = await multiFactor(user).getSession();
      setSecret(await TotpMultiFactorGenerator.generateSecret(session));
      setNeedsPasswordReauth(false);
      setReauthPassword("");
      setCode("");
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnrollment = async (event) => {
    event.preventDefault();
    if (!secret || code.length !== 6) return;
    setBusy(true);
    setError("");

    if (isDemoMode) {
      localStorage.setItem(DEMO_MFA_STORAGE_KEY, "true");
      setDemoMfaEnrolled(true);
      setSecret(null);
      setCode("");
      setMessage("2-Step Verification (MFA) is now enabled for this account.");
      setBusy(false);
      return;
    }

    try {
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(secret, code);
      await multiFactor(user).enroll(assertion, "Authenticator app");
      setSecret(null);
      setCode("");
      refresh();
      setMessage("2-Step Verification (MFA) is now enabled for this account.");
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const removeFactor = async (factor) => {
    setBusy(true);
    setError("");
    setMessage("");

    if (isDemoMode) {
      localStorage.removeItem(DEMO_MFA_STORAGE_KEY);
      setDemoMfaEnrolled(false);
      setMessage("2-Step Verification (MFA) was removed.");
      setBusy(false);
      return;
    }

    try {
      await multiFactor(user).unenroll(factor);
      refresh();
      setMessage("2-Step Verification (MFA) was removed.");
    } catch (nextError) {
      setError(errorMessage(nextError));
    } finally {
      setBusy(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret.secretKey);
      setMessage("Setup key copied.");
    } catch {
      setError("The setup key could not be copied. Select and copy it manually.");
    }
  };

  return (
    <section className="settings-card">
      <div className="settings-card-header">
        <ShieldCheck />
        <div>
          <h2>2-Step Verification (MFA)</h2>
          <p>Optional extra layer of account security using an authenticator app (Google Authenticator, Authy, etc.).</p>
        </div>
      </div>

      <div className="security-status-list">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <CheckCircle2 className={isEnabled ? "status-good" : "status-warning"} />
            <span>
              <strong>{isEnabled ? "2-Step Verification is active" : "2-Step Verification is off"}</strong>
              <small>{isEnabled ? "6-digit authenticator code required when signing in" : "Sign in using your password or Google account only"}</small>
            </span>
          </div>
          <input
            className="vp-switch"
            type="checkbox"
            checked={isEnabled}
            disabled={busy || Boolean(secret) || needsPasswordReauth}
            onChange={(e) => {
              if (e.target.checked) {
                startEnrollment();
              } else if (isDemoMode) {
                removeFactor();
              } else if (factors.length > 0) {
                removeFactor(factors[0]);
              }
            }}
            aria-label="Toggle 2-step verification"
          />
        </div>
      </div>

      {factors.length > 0 && !isDemoMode && (
        <div className="security-status-list" style={{ marginTop: "0.5rem" }}>
          {factors.map((factor) => (
            <div key={factor.uid}>
              <CheckCircle2 className="status-good" />
              <span>
                <strong>{factor.displayName || "Authenticator app"}</strong>
                <small>Enrolled {factor.enrollmentTime || "for this account"}</small>
              </span>
              <button
                className="button button-secondary"
                type="button"
                disabled={busy}
                onClick={() => removeFactor(factor)}
              >
                <Trash2 /> Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {needsPasswordReauth && (
        <form className="settings-form" onSubmit={handlePasswordReauth} style={{ marginTop: "1rem" }}>
          <div className="notice notice-warning">Confirm your current password to continue setting up 2-step verification.</div>
          <label>
            <span>Current password</span>
            <input
              type="password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              placeholder="Enter current password"
              autoComplete="current-password"
              required
              autoFocus
            />
          </label>
          <div className="settings-form-actions">
            <button className="button button-primary" type="submit" disabled={busy || !reauthPassword}>
              <KeyRound /> {busy ? "Verifying…" : "Confirm password"}
            </button>
            <button className="button button-secondary" type="button" disabled={busy} onClick={() => setNeedsPasswordReauth(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {secret && (
        <form className="settings-form" onSubmit={confirmEnrollment} style={{ marginTop: "1rem" }}>
          <label>
            <span>Manual setup key</span>
            <div className="settings-inline-control">
              <input value={secret.secretKey} readOnly aria-label="Authenticator setup key" />
              <button className="button button-secondary" type="button" onClick={copySecret}><Copy /> Copy</button>
            </div>
            <small>In your authenticator app, add an account and enter this secret key manually.</small>
          </label>
          <label>
            <span>Verification code</span>
            <input
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              placeholder="000000"
              required
              autoFocus
            />
          </label>
          <div className="settings-form-actions">
            <button className="button button-primary" type="submit" disabled={busy || code.length !== 6}>
              Verify and enable
            </button>
            <button className="button button-secondary" type="button" disabled={busy} onClick={() => { setSecret(null); setCode(""); }}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {message && <div className="notice notice-success" role="status" style={{ marginTop: "0.75rem" }}>{message}</div>}
      {error && <div className="notice notice-danger" role="alert" style={{ marginTop: "0.75rem" }}>{error}</div>}
    </section>
  );
}
